import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Cookie, BarChart3, Megaphone, Eye, Database, UserCheck, Clock, Mail } from 'lucide-react';
import { useCookieConsent } from '../components/CookieConsent';
import { Button } from '../components/ui/button';

const PrivacyPage = () => {
    const { consent, resetConsent } = useCookieConsent();

    // Scroll to top on page load
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-900">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Retour à l'accueil
                    </Link>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                            <Shield className="w-7 h-7 text-blue-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900">Politique de Confidentialité et Cookies</h1>
                            <p className="text-slate-500">Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
                        </div>
                    </div>

                    {/* Table des matières */}
                    <nav className="bg-slate-50 rounded-xl p-6 mb-8">
                        <h2 className="font-semibold text-slate-900 mb-3">Sommaire</h2>
                        <ol className="space-y-2 text-sm text-blue-600">
                            <li><a href="#responsable" className="hover:underline">1. Responsable du traitement</a></li>
                            <li><a href="#donnees-collectees" className="hover:underline">2. Données collectées</a></li>
                            <li><a href="#finalites" className="hover:underline">3. Finalités du traitement</a></li>
                            <li><a href="#base-legale" className="hover:underline">4. Base légale</a></li>
                            <li><a href="#destinataires" className="hover:underline">5. Destinataires des données</a></li>
                            <li><a href="#duree-conservation" className="hover:underline">6. Durée de conservation</a></li>
                            <li><a href="#cookies" className="hover:underline">7. Cookies et traceurs</a></li>
                            <li><a href="#droits" className="hover:underline">8. Vos droits</a></li>
                            <li><a href="#securite" className="hover:underline">9. Sécurité</a></li>
                            <li><a href="#contact" className="hover:underline">10. Contact</a></li>
                        </ol>
                    </nav>

                    <div className="prose prose-slate max-w-none space-y-8">
                        
                        {/* 1. Responsable */}
                        <section id="responsable">
                            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900 mb-4">
                                <Database className="w-5 h-5 text-blue-600" />
                                1. Responsable du traitement
                            </h2>
                            <div className="bg-slate-50 rounded-xl p-5">
                                <p className="mb-2">Le responsable du traitement des données personnelles est :</p>
                                <p className="font-medium">ECO MOBILE SOLUTION</p>
                                <p className="text-sm text-slate-600">SAS au capital de 1 000 €</p>
                                <p className="text-sm text-slate-600">SIREN : 982 341 869</p>
                                <p className="text-sm text-slate-600">ZI N2 CD 70 Le Biplan, Route d'Hérin, 59220 Rouvignies, France</p>
                                <p className="text-sm text-slate-600 mt-2">Email : <a href="mailto:welcome@phoneb2b.com" className="text-blue-600">welcome@phoneb2b.com</a></p>
                            </div>
                        </section>

                        {/* 2. Données collectées */}
                        <section id="donnees-collectees">
                            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900 mb-4">
                                <Eye className="w-5 h-5 text-blue-600" />
                                2. Données collectées
                            </h2>
                            <p>Dans le cadre de notre activité B2B, nous collectons les données suivantes :</p>
                            
                            <h3 className="font-semibold mt-4 mb-2">Données d'identification professionnelle</h3>
                            <ul className="list-disc list-inside text-slate-700 space-y-1">
                                <li>Raison sociale, SIREN/SIRET, numéro de TVA</li>
                                <li>Nom et prénom du contact</li>
                                <li>Adresse email professionnelle</li>
                                <li>Numéro de téléphone, WhatsApp (optionnel)</li>
                                <li>Adresses de facturation et livraison</li>
                            </ul>

                            <h3 className="font-semibold mt-4 mb-2">Données relatives à votre activité</h3>
                            <ul className="list-disc list-inside text-slate-700 space-y-1">
                                <li>Type d'activité (réparateur, grossiste, e-commerçant...)</li>
                                <li>Niveau de réparation</li>
                                <li>Volume estimé d'achats</li>
                                <li>Nombre de points de vente</li>
                            </ul>

                            <h3 className="font-semibold mt-4 mb-2">Données de connexion et navigation</h3>
                            <ul className="list-disc list-inside text-slate-700 space-y-1">
                                <li>Adresse IP</li>
                                <li>Type de navigateur et appareil</li>
                                <li>Pages visitées et actions effectuées</li>
                                <li>Données de cookies (voir section 7)</li>
                            </ul>
                        </section>

                        {/* 3. Finalités */}
                        <section id="finalites">
                            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900 mb-4">
                                <UserCheck className="w-5 h-5 text-blue-600" />
                                3. Finalités du traitement
                            </h2>
                            <p>Vos données sont collectées pour les finalités suivantes :</p>
                            <ul className="list-disc list-inside text-slate-700 space-y-1 mt-2">
                                <li>Création et gestion de votre compte professionnel</li>
                                <li>Vérification de votre statut professionnel (SIRET)</li>
                                <li>Traitement de vos commandes et livraisons</li>
                                <li>Facturation et gestion comptable</li>
                                <li>Communication relative à vos commandes</li>
                                <li>Service après-vente et gestion des retours</li>
                                <li>Amélioration de nos services et de votre expérience</li>
                                <li>Respect de nos obligations légales</li>
                            </ul>
                        </section>

                        {/* 4. Base légale */}
                        <section id="base-legale">
                            <h2 className="text-xl font-bold text-slate-900 mb-4">4. Base légale du traitement</h2>
                            <p>Le traitement de vos données repose sur :</p>
                            <ul className="list-disc list-inside text-slate-700 space-y-1 mt-2">
                                <li><strong>L'exécution du contrat</strong> : gestion de votre compte, commandes, livraisons</li>
                                <li><strong>L'intérêt légitime</strong> : amélioration de nos services, prévention de la fraude</li>
                                <li><strong>Le consentement</strong> : cookies non essentiels, communications marketing</li>
                                <li><strong>L'obligation légale</strong> : facturation, conservation des données comptables</li>
                            </ul>
                        </section>

                        {/* 5. Destinataires */}
                        <section id="destinataires">
                            <h2 className="text-xl font-bold text-slate-900 mb-4">5. Destinataires des données</h2>
                            <p>Vos données peuvent être transmises aux destinataires suivants :</p>
                            <ul className="list-disc list-inside text-slate-700 space-y-1 mt-2">
                                <li>Nos équipes internes (commercial, logistique, comptabilité, SAV)</li>
                                <li>Nos transporteurs partenaires pour la livraison</li>
                                <li>Notre prestataire de paiement (Stripe)</li>
                                <li>Notre prestataire de facturation (Pennylane)</li>
                                <li>Notre hébergeur (Amazon Web Services)</li>
                                <li>Les autorités compétentes en cas d'obligation légale</li>
                            </ul>
                            <p className="mt-3 text-sm text-slate-600">
                                Nous ne vendons jamais vos données à des tiers. Les transferts hors UE sont encadrés 
                                par des clauses contractuelles types ou des décisions d'adéquation.
                            </p>
                        </section>

                        {/* 6. Durée de conservation */}
                        <section id="duree-conservation">
                            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900 mb-4">
                                <Clock className="w-5 h-5 text-blue-600" />
                                6. Durée de conservation
                            </h2>
                            <div className="bg-slate-50 rounded-xl p-5 space-y-3">
                                <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                                    <span className="text-slate-700">Données de compte actif</span>
                                    <span className="font-medium">Durée de la relation + 3 ans</span>
                                </div>
                                <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                                    <span className="text-slate-700">Données de facturation</span>
                                    <span className="font-medium">10 ans (obligation légale)</span>
                                </div>
                                <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                                    <span className="text-slate-700">Cookies de consentement</span>
                                    <span className="font-medium">13 mois</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-slate-700">Cookies analytiques</span>
                                    <span className="font-medium">13 mois</span>
                                </div>
                            </div>
                        </section>

                        {/* 7. Cookies */}
                        <section id="cookies">
                            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900 mb-4">
                                <Cookie className="w-5 h-5 text-blue-600" />
                                7. Cookies et traceurs
                            </h2>
                            
                            <h3 className="font-semibold mt-4 mb-2">Qu'est-ce qu'un cookie ?</h3>
                            <p>
                                Un cookie est un petit fichier texte déposé sur votre terminal lors de la visite 
                                d'un site internet. Il permet de mémoriser des informations sur votre navigation.
                            </p>

                            <h3 className="font-semibold mt-4 mb-3">Les cookies que nous utilisons</h3>
                            
                            <div className="not-prose space-y-4">
                                {/* Essential cookies */}
                                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <Shield className="w-5 h-5 text-emerald-600" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-emerald-900 mb-1">Cookies essentiels</h4>
                                            <p className="text-sm text-emerald-700 mb-2">
                                                Indispensables au fonctionnement du site : authentification, panier, 
                                                préférences de session. Ils ne peuvent pas être désactivés.
                                            </p>
                                            <ul className="text-sm text-emerald-600 space-y-1">
                                                <li>• <strong>token</strong> : Session d'authentification</li>
                                                <li>• <strong>phoneb2b_cookie_consent</strong> : Vos choix de cookies (1 an)</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* Analytics cookies */}
                                <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <BarChart3 className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-blue-900 mb-1">Cookies analytiques</h4>
                                            <p className="text-sm text-blue-700 mb-2">
                                                Mesurent l'audience et comprennent comment vous utilisez le site 
                                                pour l'améliorer.
                                            </p>
                                            <p className="text-xs text-blue-500">
                                                Statut actuel : {consent?.analytics ? 
                                                    <span className="text-emerald-600 font-medium">Acceptés</span> : 
                                                    <span className="text-slate-500 font-medium">Refusés</span>
                                                }
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Marketing cookies */}
                                <div className="bg-purple-50 border border-purple-200 rounded-xl p-5">
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <Megaphone className="w-5 h-5 text-purple-600" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-purple-900 mb-1">Cookies marketing</h4>
                                            <p className="text-sm text-purple-700 mb-2">
                                                Proposent des publicités pertinentes et mesurent l'efficacité 
                                                des campagnes marketing.
                                            </p>
                                            <p className="text-xs text-purple-500">
                                                Statut actuel : {consent?.marketing ? 
                                                    <span className="text-emerald-600 font-medium">Acceptés</span> : 
                                                    <span className="text-slate-500 font-medium">Refusés</span>
                                                }
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <h3 className="font-semibold mt-6 mb-2">Gérer vos préférences</h3>
                            <p className="mb-4">
                                Vous pouvez à tout moment modifier vos préférences en cliquant sur le bouton ci-dessous 
                                ou en configurant votre navigateur.
                            </p>
                            <Button onClick={resetConsent} className="bg-blue-600 hover:bg-blue-700">
                                <Cookie className="w-4 h-4 mr-2" />
                                Modifier mes préférences cookies
                            </Button>
                        </section>

                        {/* 8. Vos droits */}
                        <section id="droits">
                            <h2 className="text-xl font-bold text-slate-900 mb-4">8. Vos droits</h2>
                            <p>
                                Conformément au RGPD et à la loi Informatique et Libertés, vous disposez des droits suivants :
                            </p>
                            <div className="grid sm:grid-cols-2 gap-3 mt-4 not-prose">
                                <div className="bg-slate-50 rounded-lg p-4">
                                    <p className="font-medium text-slate-900">Droit d'accès</p>
                                    <p className="text-sm text-slate-600">Obtenir une copie de vos données</p>
                                </div>
                                <div className="bg-slate-50 rounded-lg p-4">
                                    <p className="font-medium text-slate-900">Droit de rectification</p>
                                    <p className="text-sm text-slate-600">Corriger vos données inexactes</p>
                                </div>
                                <div className="bg-slate-50 rounded-lg p-4">
                                    <p className="font-medium text-slate-900">Droit à l'effacement</p>
                                    <p className="text-sm text-slate-600">Supprimer vos données (sauf obligations légales)</p>
                                </div>
                                <div className="bg-slate-50 rounded-lg p-4">
                                    <p className="font-medium text-slate-900">Droit à la portabilité</p>
                                    <p className="text-sm text-slate-600">Récupérer vos données dans un format standard</p>
                                </div>
                                <div className="bg-slate-50 rounded-lg p-4">
                                    <p className="font-medium text-slate-900">Droit d'opposition</p>
                                    <p className="text-sm text-slate-600">Vous opposer au traitement de vos données</p>
                                </div>
                                <div className="bg-slate-50 rounded-lg p-4">
                                    <p className="font-medium text-slate-900">Droit de limitation</p>
                                    <p className="text-sm text-slate-600">Limiter l'utilisation de vos données</p>
                                </div>
                            </div>
                            <p className="mt-4 text-sm text-slate-600">
                                Vous pouvez également introduire une réclamation auprès de la CNIL : 
                                <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-blue-600 ml-1">www.cnil.fr</a>
                            </p>
                        </section>

                        {/* 9. Sécurité */}
                        <section id="securite">
                            <h2 className="text-xl font-bold text-slate-900 mb-4">9. Sécurité</h2>
                            <p>
                                Nous mettons en œuvre des mesures techniques et organisationnelles pour protéger 
                                vos données contre tout accès non autorisé, perte, destruction ou altération :
                            </p>
                            <ul className="list-disc list-inside text-slate-700 space-y-1 mt-2">
                                <li>Chiffrement des données en transit (HTTPS/TLS)</li>
                                <li>Chiffrement des mots de passe (bcrypt)</li>
                                <li>Accès restreint aux données sensibles</li>
                                <li>Sauvegardes régulières</li>
                                <li>Hébergement sécurisé (AWS)</li>
                            </ul>
                        </section>

                        {/* 10. Contact */}
                        <section id="contact">
                            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900 mb-4">
                                <Mail className="w-5 h-5 text-blue-600" />
                                10. Contact
                            </h2>
                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                                <p className="text-slate-700 mb-3">
                                    Pour toute question relative à cette politique ou pour exercer vos droits, 
                                    contactez-nous :
                                </p>
                                <p className="font-medium text-slate-900">
                                    Email : <a href="mailto:welcome@phoneb2b.com" className="text-blue-600">welcome@phoneb2b.com</a>
                                </p>
                                <p className="text-sm text-slate-600 mt-2">
                                    ECO MOBILE SOLUTION<br />
                                    ZI N2 CD 70 Le Biplan, Route d'Hérin<br />
                                    59220 Rouvignies, France
                                </p>
                            </div>
                        </section>

                    </div>

                    {/* Dernière mise à jour */}
                    <p className="text-center text-sm text-slate-400 mt-8 pt-4 border-t">
                        Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>

                    {/* Retour à l'accueil */}
                    <div className="mt-8 text-center">
                        <Link 
                            to="/" 
                            onClick={() => window.scrollTo(0, 0)}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors font-medium"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Retour à l'accueil
                        </Link>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default PrivacyPage;
